// `chrome-extension.extension-tabs.client-contacts.hunter.review` — Phase 4's review screen
// (plan §7 phase 4).
//
// ⚠️ THIS FILE IS THE TEST OF THE PHASE-3 DESIGN CONSTRAINT. The plan says Phase 4 is "much
// cheaper IF Phase 3 components were built generic over entity type", and this is where that
// claim either holds or does not. What carried over unchanged:
//
//   the tabs chrome and its product name     the alert selector and its dropdown panel
//   the alert tab in the panel                the sort / filters bar
//   the selection bar and its bulk actions    pagination
//   `getAlerts(AlertEntity.ClientContacts)`   the guard, the bodyClass, the layout
//
// What did NOT, and could not:
//
//   - THE ROW. Company and job title swap places and change classes, the map pin is gone, and
//     the ATS "Prev. engagement" block has no candidate equivalent. See `ClientContactRow`.
//   - THE AUTOMATION BANNER, which upstream has COMMENTED OUT on this side (the template keeps
//     twenty lines of it behind `<!-- TODO avif: [RND-6910] remove these option? -->`). It is
//     absent here rather than hidden behind a flag, because the flag is not what makes it
//     absent.
//   - The candidates call: `clientContactPage` against `candidatePage`, different entity
//     fields, so the two API functions stay separate.
//
// So the constraint held for the chrome and the interactions and failed, correctly, for the
// one thing that is genuinely a different screen.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactElement } from 'react'
import { useLocation, useNavigate, useOutlet, useSearchParams } from 'react-router-dom'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  AlertEntity,
  fetchViewModel,
  getClientContactNumbersByAlertId,
  getAlerts,
  NEW_ALERT_ID,
  getClientContactsByAlertId,
  getClientContactDetailsByAlertId,
  deleteAlertById,
  getClientContactEmailIntegrationDetails,
  favorClientContacts,
  hideClientContacts,
  unfavorClientContact,
  unhideClientContact,
  type AlertClientContact,
  type AlertClientContactDetails,
  type ExtensionAlert,
  type ExtensionAlertContainer,
} from '@woo-ext/api'
import {
  AlertContainerHost,
  bulkActions,
  type BulkActionFlags,
  CandidateListHost,
  CandidateFiltersBar,
  CandidatePagination,
  CandidateSelectionBar,
  ExtensionAlertsHost,
  ClientContactRow,
  MessageHistoryTooltip,
  useMessageHistory,
  ClientContactFiltersTooltip,
  buildClientContactFilter,
  contactShownOptionForAlert,
  contactShownOptionsFor,
  contactSortOptionForAlert,
  CONTACT_SORT_OPTIONS,
  type ClientContactShowFilterOption,
  type ClientContactSortOption,
  SlimHeaderDropdown,
  SlimHeaderDropdownPanel,
  DeleteAlertDialogue,
  MultiSelectModal,
  ExportToAtsModal,
  NoValidCandidatesDialogue,
  hasValidIdsForMessagingOrEmpty,
  applyFavor,
  applyHide,
  clientContactName,
  applyUnfavor,
  applyUnhide,
  archivedRowsStay,
  nextRowAfterArchive,
  withoutRows,
  addToJobTitle,
  exportTitle,
  profileActions,
  useDismissOnOutsideClick,
  ProfileAction,
  type BulkAction,
  type ProfileActionDefinition,
  getAtsLabel,
  singleProfileExportEnabled,
} from '@woo-ext/ui'
import {
  clearCheckedEntities,
  getCheckedEntities,
  getCheckedVersion,
  setCheckedEntities,
  subscribeToCheckedEntities,
  type CheckedEntity,
} from './entityCheckboxContainer.ts'
import { inheritedData, inheritedString } from './routeData.ts'
import { trackClientContact, trackClientContactBulk, trackClientContactList, trackClientContactSelection } from './actionTracking.ts'
import { sendOpenHotlist } from './assignToJob.ts'
import { useShortlistExportRequests, useShortlistFlagEvents, useShortlistMessageRequests } from './shortlistEvents.ts'
import {
  ExtensionProduct,
  contactAlertFieldsFromParams,
  getUnexpiredLastAlertViewed,
  isRestorable,
  persistLastAlertViewed,
  restoredPage,
  restoredSortBy,
  scrollToRestoredRow,
  sessionUserIdOf,
  type ExtensionProductLastAlertViewedState,
} from './lastAlertViewed.ts'

/**
 * `shortlistHunterAutomationService.isEnabled` — `featureFlags[HUNTER_AUTOMATION] &&
 * careero.hunterAutomationEnabled`.
 *
 * ⚠️ IT APPLIES TO CONTACTS TOO, which is where this screen went wrong. The panel's
 * `automatedHunters` is correctly false here — the job-search group belongs to candidates —
 * but the dot beside each search name comes from the automation SERVICE, which has no notion
 * of product. Production's contacts dropdown renders `hunter-automation-mode mode-disabled`
 * on every row; this rendered none.
 */
const HUNTER_AUTOMATION = 'HUNTER_AUTOMATION'

function useAlertId(): number | undefined {
  const [params] = useSearchParams()
  const raw = params.get('alertId')
  if (raw === null) return undefined
  const parsed = Number.parseInt(raw, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

/** `pageSize = 10` — `extension-alert-client-contacts.component.ng1.ts`, as on candidates. */
const CONTACT_PAGE_SIZE = 10

/**
 * The row whose preview the list opened (`selectedCandidateId` / `selectedClientContactId`).
 * Module state rather than a ref because opening a side panel REMOUNTS the review, and the value
 * has to outlive that — the same reason the selection lives in `entityCheckboxContainer`.
 */
const selectedRow: { current: number | undefined } = { current: undefined }

/** The last search `HunterResultsLoaded` was recorded for, in this document. */
let lastRecordedSearch: string | undefined

/**
 * `sendNewSearchMadeAction` (component:2025) — `HunterResultsLoaded`, once per `startNewSearch()`.
 * The same second, non-persisting details call as the candidate list, and the same once-per-query
 * rule, for the same reasons: only that call answers `totalEnriched`, and upstream restores a
 * returning list from storage without searching. See `recordCandidateSearch` in ShortlistReview.
 */
function recordClientContactSearch(
  alertId: number,
  sortBy: ClientContactSearchQuery['sortBy'],
  filter: ClientContactSearchQuery['filter'],
  force = false,
): void {
  const key = JSON.stringify([alertId, sortBy, filter])
  if (!force && key === lastRecordedSearch) return
  lastRecordedSearch = key
  getClientContactDetailsByAlertId({ alertId, sortBy, filter }).then(
    (details) => {
      trackClientContactList.resultsLoaded(alertId, details)
    },
    () => undefined,
  )
}

type ClientContactSearchQuery = Parameters<typeof getClientContactDetailsByAlertId>[0]
/** `MAX_RESULTS = 1000`, the server's own ceiling. */
const CONTACT_MAX_RESULTS = 1000

export function ClientContactReview(): ReactElement {
  const alertId = useAlertId()
  const navigate = useNavigate()
  // ⚠️ `rendered` IS "A CHILD IS ACTIVE", NOT "THE SIDE VIEW EXISTS". Upstream's condition is
  // `$state.includes(hunterReview) && !$state.is(hunterReview)` — inside the subtree but not at
  // its root — and without the class the div is `flex: 0`, a zero-width box that takes no space.
  // `useOutlet()` is null for exactly the same set of routes, so the two agree by construction
  // rather than by a second list of child paths that could drift.
  // ⚠️ THE CALL ITSELF IS FURTHER DOWN, because its context needs `contacts` and `current`.
  // See `sideView` below.

  // `data.isCheckboxDependent` — declared by `review/export` and `review/client-contact-messages`
  // and read here; see the same block on the candidate screen for why it comes off the route.
  const { pathname } = useLocation()
  const [routeParams] = useSearchParams()
  const checkboxDependent = inheritedData(pathname, 'isCheckboxDependent') === true
  const checkboxOperationLabel = inheritedString(pathname, 'checkboxOperationLabel')
  const [containers, setContainers] = useState<ExtensionAlertContainer[] | undefined>(undefined)
  const [automationEnabled, setAutomationEnabled] = useState(false)
  /**
   * `extensionAlertsTabsData` — the per-alert counts every row in the dropdown shows.
   *
   * ⚠️ THE CONTACTS PANEL NEVER FETCHED THEM, so each row was a bare name where production
   * reads "Contacts · 102 client contacts". The candidate screen has had this since the
   * dropdown landed; this product's copy of the wiring did not.
   */
  const [tabData, setTabData] = useState<
    Record<number, { totalResults: number; totalNewTopMatchesResults: number; totalNewTopMatchesSinceLastViewedAlerts: number }>
  >({})
  const [contacts, setContacts] = useState<AlertClientContact[] | undefined>(undefined)
  /** `data.allRelevantDetails`, filled by "select all results"; the messaging check reads it. */
  const [allDetails, setAllDetails] = useState<readonly AlertClientContactDetails[]>([])
  const [noValidContactsOpen, setNoValidContactsOpen] = useState(false)
  /** The ATS export dialogue's done-button copy is chosen by it. */
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  /** The ids an open ATS export dialogue is exporting. */
  const [exportingToAts, setExportingToAts] = useState<readonly number[] | undefined>(undefined)
  /** The bulk action waiting on `verifyHasSelectedClientContacts`' modal. */
  const [multiSelectAction, setMultiSelectAction] = useState<BulkAction | undefined>(undefined)
  // The dev tenant every reference capture comes from has this ON, and production has it OFF
  // — see the note on the candidate screen. Same flag, same reason, and it was the same §4.1
  // dependency: `ENV` reached the tenant on 16 Sep 2026, so this now resolves to true here and
  // the contact rows carry their `(100)` suffix. 668 pixels to 205.
  const [showAlgorithm, setShowAlgorithm] = useState(false)
  const [messagingEnabled, setMessagingEnabled] = useState(false)
  // The envelope's Message History — `openClientContactMessagesTooltip`.
  const messageHistory = useMessageHistory('clientContacts')
  const [actionFlags, setActionFlags] = useState<BulkActionFlags>({})
  /** See `ShortlistReview`: two export flags, and only one of them has a screen. */
  const [exportToExistingList, setExportToExistingList] = useState(false)
  /**
   * The bar's two controls, which had no state at all: `CandidateFiltersBar` was rendered with
   * three constants and no handlers, so Sort and Filters were decoration — and the Sort menu,
   * had it opened, would have offered the CANDIDATE orders. See `clientContactFilters.ts`.
   */
  const [sortOption, setSortOption] = useState<ClientContactSortOption>(() => contactSortOptionForAlert(undefined))
  const [shownOption, setShownOption] = useState<ClientContactShowFilterOption>(() =>
    contactShownOptionForAlert(undefined),
  )
  const [sortOpen, setSortOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  /** `persistFilterAndSort` is a write; it is sent only once the USER has chosen something. */
  const [sortChosen, setSortChosen] = useState(false)
  const [filtersChosen, setFiltersChosen] = useState(false)
  const [actionedViewEnabled, setActionedViewEnabled] = useState(false)
  /** `ctrl.currPage` — the contacts twin of the review screen's pager. */
  const [currentPage, setCurrentPage] = useState(1)
  /** The session's `userId`, which `reloadStateIfNeeded` matches — see the candidate screen. */
  const [sessionUserId, setSessionUserId] = useState<number | null | undefined>(undefined)
  /** `reloadStateIfNeeded`'s verdict for the alert on screen — see the candidate screen. */
  const [seed, setSeed] = useState<
    { alertId: number; restore: ExtensionProductLastAlertViewedState | null } | undefined
  >(undefined)
  /** The ☰'s own menu, and the alert list behind the name — two different dropdowns. */
  const [slimActionsOpen, setSlimActionsOpen] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  /**
   * `ctrl.checkedClientContactIdsMap` and the two menus over it.
   *
   * ⚠️ THIS SCREEN HAD NO SELECTION MODEL AT ALL. The selection bar rendered its count, its
   * checkbox and its action icons, and `CandidateSelectionBar` forwards every handler — but
   * none was passed, so the bar was decoration: the ⋮ opened nothing, the row checkboxes
   * toggled nothing, and "Select all" had no list to act on. The candidates screen has had
   * one since Phase 3; this is the same model against the contacts endpoints.
   */
  /**
   * ⚠️ NOT `useState`, FOR THE REASON THE CANDIDATES SCREEN DOCUMENTS — opening a side-view
   * panel unmounts and remounts this screen, so a selection held in component state never
   * reaches the panel the bulk action opened. See `entityCheckboxContainer.ts`.
   */
  const checkedVersion = useSyncExternalStore(subscribeToCheckedEntities, getCheckedVersion)
  /** `ctrl.profileActions` — the hover strip's icons, built from the same bootstrap. */
  const [rowActions, setRowActions] = useState<readonly ProfileActionDefinition[]>([])
  const [selectMenuOpen, setSelectMenuOpen] = useState(false)
  const [bulkOverflowOpen, setBulkOverflowOpen] = useState(false)
  useDismissOnOutsideClick({
    open: selectMenuOpen,
    onDismiss: () => {
      setSelectMenuOpen(false)
    },
    within: ['.candidate-selection-container'],
  })
  useDismissOnOutsideClick({
    open: bulkOverflowOpen,
    onDismiss: () => {
      setBulkOverflowOpen(false)
    },
    within: ['.candidate-selection-bulk-actions-more'],
  })
  useDismissOnOutsideClick({
    open: sortOpen,
    onDismiss: () => {
      setSortOpen(false)
    },
    within: ['.slim-filters-action'],
  })
  useDismissOnOutsideClick({
    open: filtersOpen,
    onDismiss: () => {
      setFiltersOpen(false)
    },
    within: ['.slim-filters-action', '.tooltip'],
  })
  useDismissOnOutsideClick({
    open: panelOpen,
    onDismiss: () => {
      setPanelOpen(false)
    },
    within: ['.shortlist-header-slim-alert-name-hover-ctas', '.tooltip'],
  })
  useDismissOnOutsideClick({
    open: slimActionsOpen,
    onDismiss: () => {
      setSlimActionsOpen(false)
    },
    within: ['.more-options-caret'],
  })
  // `maxResults` is the TOTAL behind the alert, not the page length — it is what the count
  // beside the user icon shows, and the page is a slice of it.
  const [totalResults, setTotalResults] = useState<number | undefined>(undefined)
  /**
   * The ☰'s Delete, which asks first — see the candidates screen.
   *
   * ⚠️ DECLARED WITH THE OTHER STATE, ABOVE THE EARLY RETURN. This screen returns a bare div
   * while `containers` is undefined, so a hook added further down runs on some renders and not
   * others — React error #310, "rendered more hooks than during the previous render", and the
   * whole panel fails to bootstrap. It is not a style preference; it is why this screen was
   * blank for one measurement.
   */
  const [deleting, setDeleting] = useState<ExtensionAlert | undefined>(undefined)

  useEffect(() => {
    let live = true
    getAlerts(AlertEntity.ClientContacts).then(
      (loaded) => {
        if (live) setContainers(loaded)
      },
      () => {
        // As on the candidate side: upstream's error modal has not landed.
      },
    )
  return () => {
      live = false
    }
  }, [])

  // ⚠️ A RESTORE IS THE OTHER BRANCH (`reloadPreviousSearchFromState`, component:1169) — the
  // stored ids as `limitClientContactIds`, no persist, the stored page, nothing recorded. See the
  // candidate screen, which this mirrors.
  useEffect(() => {
    const id = containers?.find((container) => container.alert.id === alertId)?.alert.id ?? containers?.[0]?.alert.id
    if (id === undefined || seed?.alertId !== id) return undefined
    let live = true
    const restore = sortChosen || filtersChosen ? null : seed.restore
    const sortBy = sortOption.field
    const filter = buildClientContactFilter(shownOption)
    getClientContactsByAlertId({
      alertId: id,
      sortBy,
      filter,
      ...(restore !== null ? { limitClientContactIds: restore.allEntityIds } : {}),
      ...(sortChosen || filtersChosen ? { persistFilterAndSort: true } : {}),
    }).then(
      (list) => {
        if (!live) return
        setContacts(list.clientContactPage)
        setTotalResults(list.maxResults)
        if (restore !== null) {
          setCurrentPage(restoredPage(restore.selectedPage, list.clientContactPage.length, CONTACT_PAGE_SIZE))
          const selected = restore.selectedEntityId
          if (selected !== undefined) window.setTimeout(() => scrollToRestoredRow(selected), 750)
          return
        }
        setCurrentPage(1)
        // `persistState` at the end of `startNewSearch` — with the open preview as the row, as
        // on the candidate screen, because this one keeps it open where upstream closes it.
        selectedRow.current = openContactRef.current
        void persistLastAlertViewed(ExtensionProduct.ClientContacts, {
          ...(typeof sessionUserId === 'number' ? { userId: sessionUserId } : {}),
          selectedAlertId: id,
          ...(openContactRef.current === undefined ? {} : { selectedEntityId: openContactRef.current }),
          allEntityIds: list.clientContactPage.map((contact) => contact.id),
          selectedPage: 1,
          getDataParams: { alertId: id, sortBy, filter: { ...filter } },
        })
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [containers, alertId, seed, sessionUserId, sortOption, shownOption, sortChosen, filtersChosen])

  // `HunterResultsLoaded` for every search the list makes — see `recordClientContactSearch`. Not
  // for a restore.
  useEffect(() => {
    const id = containers?.find((container) => container.alert.id === alertId)?.alert.id ?? containers?.[0]?.alert.id
    if (id === undefined || seed?.alertId !== id) return
    if (seed.restore !== null && !sortChosen && !filtersChosen) return
    recordClientContactSearch(id, sortOption.field, buildClientContactFilter(shownOption))
  }, [containers, alertId, seed, sortOption, shownOption, sortChosen, filtersChosen])

  /** `setBulkProfileActions` assembles the list per tenant — see `contactShownOptionsFor`. */
  const shownOptions = useMemo(
    () =>
      contactShownOptionsFor({
        exportClientContactsEnabled: actionFlags.exportCandidatesEnabled === true,
        messageClientContactsEnabled: actionFlags.messageCandidatesEnabled === true,
        actionedAndUnactionedEnabled: actionedViewEnabled,
      }),
    [actionFlags.exportCandidatesEnabled, actionFlags.messageCandidatesEnabled, actionedViewEnabled],
  )

  /**
   * `syncFiltersAndSorting()` (`:635`) — run once per alert and NOT on every render, exactly as
   * on the candidate screen: the server PERSISTS the view per alert, so an alert the recruiter
   * last left on "Archived" has to come back on "Archived" rather than on a bar that says
   * "All" above archived rows. Re-running it after a change would put the old value straight
   * back.
   */
  // ⚠️ KEYED ON THE OPTION LIST AS WELL AS THE ALERT, because the two arrive separately. The
  // list grows when the bootstrap lands (Exported, Messaged, All + Actioned), and a sync that
  // ran before it did would fail to find an alert persisted on one of those and silently fall
  // back to "All" — the bar then disagreeing with the rows the server returns.
  //
  // ⚠️ AND `reloadStateIfNeeded` DECIDES FIRST, ONCE PER ALERT. The stored state is read on the
  // alert's first sync and the verdict reused when the option list grows: re-reading it then
  // would find the state the first sync's new search has just written, and turn the same mount's
  // second fetch into a restore.
  const syncedKey = useRef<string | undefined>(undefined)
  const restoreDecision = useRef<
    { alertId: number; verdict: Promise<ExtensionProductLastAlertViewedState | null> } | undefined
  >(undefined)
  useEffect(() => {
    const alert = containers?.find((container) => container.alert.id === alertId)?.alert ?? containers?.[0]?.alert
    if (alert === undefined || sessionUserId === undefined) return
    const key = `${String(alert.id)}:${String(shownOptions.length)}`
    if (syncedKey.current === key) return
    syncedKey.current = key
    if (restoreDecision.current?.alertId !== alert.id) {
      restoreDecision.current = {
        alertId: alert.id,
        verdict: getUnexpiredLastAlertViewed(ExtensionProduct.ClientContacts)
          .catch(() => null)
          .then((stored) =>
            // `moveToClientContactId` is never set in this port — see the candidate screen.
            isRestorable(stored, { userId: sessionUserId ?? undefined, alertId: alert.id }) ? stored : null,
          ),
      }
    }
    void restoreDecision.current.verdict.then((restore) => {
      if (syncedKey.current !== key) return
      if (restore !== null) {
        // `resetGetAlertDataParamsToState` — the stored query, not the alert's.
        setSortOption(contactSortOptionForAlert(restoredSortBy(ExtensionProduct.ClientContacts, restore.getDataParams)))
        setShownOption(contactShownOptionForAlert({ ...alert, ...contactAlertFieldsFromParams(restore.getDataParams) }, shownOptions))
        selectedRow.current = restore.selectedEntityId
      } else {
        setSortOption(contactSortOptionForAlert(alert.sortBy))
        setShownOption(contactShownOptionForAlert(alert, shownOptions))
      }
      setSortChosen(false)
      setFiltersChosen(false)
      setSeed({ alertId: alert.id, restore })
    })
    // ⚠️ THE PER-ALERT CLEAR IS GONE HERE TOO, and for the same measured reason as on the
    // candidates screen: this effect re-runs on every remount, and opening a side-view panel
    // remounts the screen. `entityCheckboxContainer` keys by alert id, so the guarantee holds
    // without a reset that cannot tell a new alert from a new mount.
  }, [containers, alertId, shownOptions, sessionUserId])

  // `showAlgorithm = ENV.IS_DEV || ENV.IS_TEST` — an ENVIRONMENT flag, not a feature flag
  // (extension-alert-client-contacts.component.ng1.ts mirrors the candidate component here).
  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setSessionUserId(sessionUserIdOf(model.session))
        setShowAlgorithm(model.ENV?.IS_DEV === true || model.ENV?.IS_TEST === true)
        setAutomationEnabled(
          model.featureFlags?.[HUNTER_AUTOMATION] === true && model.hunterAutomationEnabled === true,
        )
        // The contact bar reads the SAME flags as the candidate bar — there is no
        // contact-specific feature flag anywhere in the provider; see `BulkActionFlags`.
        const messaging = model.featureFlags?.['SHORTLIST_MESSAGE_CANDIDATES'] === true
        setMessagingEnabled(messaging)
        const exportFlag = model.featureFlags?.['EXPORT_CANDIDATES'] === true
        setActionedViewEnabled(model.featureFlags?.['SHORTLIST_ACTIONED_AND_UNACTIONED_VIEW'] === true)
        // `viaHunter: true` — this is the contacts hunter review, which is what puts Favour
        // and Archive in the strip. No Add-to-job or Download on this product.
        setRowActions(
          profileActions({
            viaHunter: true,
            messageCandidatesEnabled: messaging,
            // ⚠️ NO REPORT ON A CONTACT ROW. The contacts screen passes the provider no
            // `reportEnrichment` handler, so upstream's strip never draws it (measured 28 Oct
            // beside build 354); enabling it here drew a sixth icon that did nothing.
            reportEnrichmentEnabled: false,
            singleProfileExportEnabled: singleProfileExportEnabled(model),
            exportTitle: exportTitle(model.atsName ?? undefined),
            atsLabel: getAtsLabel(model),
          }),
        )
        setExportToExistingList(exportFlag && model.exportCandidatesToExistingListEnabled === true)
        setAtsName(model.atsName ?? undefined)
        setActionFlags({
          // See `atsCopy` — the contacts bar names the ATS in the same two places.
          addToJobTitle: addToJobTitle(model.atsName ?? undefined),
          exportTitle: exportTitle(model.atsName ?? undefined),
          // ⚠️ NO `addToAtsJobEnabled`, AND NOT BECAUSE THE FLAG IS OFF. Upstream builds the
          // bulk bar from the HANDLERS each screen supplies, and the contacts screen supplies
          // no add-to-job one (`extension-alert-client-contacts.component.ng1.ts:411`) — you
          // do not add a contact to a job. This port drove the bar from feature flags alone
          // and the tenant has `ADD_TO_JOB` on, so contacts offered it: measured against
          // build 354, prod's bar read "Export to Bullhorn hotlist/tearsheet · Send a
          // message" and this one "Add to Bullhorn job · Send a message" — the extra action
          // also pushing Export off the inline row into the overflow.
          exportCandidatesEnabled:
            exportFlag &&
            (model.exportCandidatesEnabled === true || model.exportCandidatesToExistingListEnabled === true),
          messageCandidatesEnabled: messaging,
          // Nor `exportToFileEnabled`: the contacts screen's CSV handler
          // (`onExportClientContactsToCsvClicked`) is commented out upstream, so that action
          // is not offered here either. It happens to be off on this tenant anyway, which is
          // exactly why it needed stating rather than leaving to the flag.
          atsLabel: getAtsLabel(model),
        })
      },
      () => {
        // With no user to compare, a stored state stamped with one is not restored.
        if (live) setSessionUserId(null)
      },
    )
    return () => {
      live = false
    }
  }, [])

  /**
   * ⚠️ BOTH OF THESE SIT ABOVE THE `containers` GUARD BECAUSE THEY ARE HOOKS, and the guard
   * below returns early — a `useMemo` or `useOutlet` under it runs on some renders and not
   * others, which React treats as a changed hook order and throws on. The candidates screen
   * has no such guard above its own copy, so the two blocks look gratuitously different.
   *
   * The selection has to reach the side view because `ExportPanelRoute` reads it from context:
   * the panel is a child route here, where upstream's `entityCheckboxContainer` is a service
   * that both the list and the panel inject. `name` travels too — the panel's heading names a
   * single contact rather than counting one.
   */
  const selectedAlert = containers?.find((container) => container.alert.id === alertId) ?? containers?.[0]
  const selectedAlertId = selectedAlert?.alert.id

  const checked = useMemo(
    () => getCheckedEntities(selectedAlertId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkedVersion IS the dependency
    [selectedAlertId, checkedVersion],
  )
  const selectedIds = useMemo(() => new Set(checked.map((entry) => entry.id)), [checked])

  /** The list's ids, shortened by an archive before the next row persists — see the candidates. */
  const listIdsRef = useRef<readonly number[]>([])
  listIdsRef.current = (contacts ?? []).map((contact) => contact.id)

  /** `persistState(clientContactId, …)` (component:1491) — see `persistListState` on the candidates. */
  const persistListState = (selectedId: number | undefined, page: number = currentPage): void => {
    const alert = selectedAlert?.alert
    if (alert === undefined || seed?.alertId !== alert.id || contacts === undefined) return
    void persistLastAlertViewed(ExtensionProduct.ClientContacts, {
      ...(typeof sessionUserId === 'number' ? { userId: sessionUserId } : {}),
      selectedAlertId: alert.id,
      ...(selectedId === undefined ? {} : { selectedEntityId: selectedId }),
      allEntityIds: [...listIdsRef.current],
      selectedPage: page,
      getDataParams: { alertId: alert.id, sortBy: sortOption.field, filter: { ...buildClientContactFilter(shownOption) } },
    })
  }

  const setSelectedIds = (
    update: ReadonlySet<number> | ((previous: ReadonlySet<number>) => ReadonlySet<number>),
  ): void => {
    if (selectedAlertId === undefined) return
    const next = typeof update === 'function' ? update(selectedIds) : update
    const known = new Map<number, CheckedEntity>(checked.map((entry) => [entry.id, entry]))
    setCheckedEntities(
      selectedAlertId,
      [...next].map((id) => {
        const already = known.get(id)
        if (already !== undefined) return already
        const row = contacts?.find((contact) => contact.id === id)
        return { id, name: row === undefined ? undefined : clientContactName(row) }
      }),
    )
  }

  /** `exportClientContactsDecisionData` — mark the rows, then empty the selection. */
  const onExported = (exportedIds: readonly number[]): void => {
    const ids = new Set(exportedIds)
    if (selectedAlertId !== undefined) trackClientContactBulk.exported(selectedAlertId, exportedIds, contacts ?? [])
    setContacts((previous) =>
      previous?.map((row) => (ids.has(row.id) ? { ...row, exported: true, viewed: true } : row)),
    )
    clearCheckedEntities(selectedAlertId)
  }
  const sideView = useOutlet(
    selectedAlert === undefined
      ? undefined
      : { alertName: selectedAlert.alert.name, alertId: selectedAlert.alert.id, checked, onExported },
  )

  /**
   * `previewClientContactResume` (`extension-alert-client-contacts.component.ng1.ts:1437`) —
   * the contacts twin of `openCandidate`, and it differed from this port in three ways at
   * once. Measured beside build 354 on the same row:
   *
   * - **The target is `/more`, not the bare profile.** `snippetState` on this screen is
   *   `ClientContactHunterReviewContactProfileMore` (`:216`) — the wide snippet — where this
   *   navigated to `…/client-contact/profile` and opened the narrow one.
   * - **`algoScore` travels with it.** Without it the destination's `isInViewMoreState()`
   *   compares against `NaN`, which matches nothing, and the More view renders closed — the
   *   same trap documented on the candidate profile.
   * - **It is a TOGGLE.** `isPreviewShown` closes an open preview rather than re-opening it,
   *   so the row is how the preview is dismissed; and like the candidates' it needs the
   *   profile CHILD route active, not just the param on the URL.
   */
  const previewRouteActive = pathname.includes('/review/client-contact/profile')
  const rawOpenContact = routeParams.get('clientContactId')
  const openContactId =
    !previewRouteActive || rawOpenContact === null ? undefined : Number.parseInt(rawOpenContact, 10)
  /** For the list's fetch, which must not refetch because a preview opened. */
  const openContactRef = useRef(openContactId)
  openContactRef.current = openContactId

  const openContact = (contactId: number, algorithm?: number): void => {
    const alert = current?.alert
    if (alert === undefined) return
    if (openContactId === contactId) {
      selectedRow.current = undefined
      void navigate(`/chrome-extension/extension/client-contact/review?alertId=${String(alert.id)}`)
      return
    }
    selectedRow.current = contactId
    // `persistState(clientContact.id, true)` — the row is the selection a reload comes back to.
    persistListState(contactId)
    // Upstream's string, order and default included — it is the page view's URL. Upstream's own
    // `$state.href` for this state answers `?originSnippet=0&clientContactId=…&algoScore=…&alertId=…`;
    // see `openCandidate` in ShortlistReview.
    const query = new URLSearchParams({ originSnippet: '0', clientContactId: String(contactId) })
    if (algorithm !== undefined) query.set('algoScore', String(algorithm))
    query.set('alertId', String(alert.id))
    void navigate(`/chrome-extension/extension/client-contact/review/client-contact/profile/more?${query.toString()}`)
  }

  /**
   * `OnClientContactHidden` (component:1661) → `removeClientContactsFromListAndRefreshPage` (:697),
   * for every archive — row, bar or profile. See `hideFromList.ts`.
   *
   * ⚠️ WITH NO NEXT ROW THE PREVIEW STAYS OPEN, unlike the candidates screen, which closes it.
   * Measured beside build 354: the contacts review persists its state with its own suppression,
   * so an archived contact's profile is left where it is.
   */
  const onContactsHidden = (alertId: number, ids: readonly number[], hiddenDate: number): void => {
    const idSet = new Set(ids)
    const stay = archivedRowsStay({
      showHidden: shownOption.showHidden,
      showExportedOnly: shownOption.showExportedClientContactsOnly,
      showMessagedOnly: shownOption.showMessagedClientContactsOnly,
    })
    if (stay) {
      setContacts((previous) => previous?.map((row) => (idSet.has(row.id) ? applyHide(row, hiddenDate) : row)))
      return
    }
    const next = removeFromList(alertId, ids)
    if (ids.some((id) => selectedIds.has(id))) {
      setSelectedIds(new Set([...selectedIds].filter((id) => !idSet.has(id))))
    }
    if (next !== undefined) openContact(next.id, next.algorithm)
  }

  /** The list half of `removeClientContactsFromListAndRefreshPage`: rows, total, tab count. */
  const removeFromList = (alertId: number, ids: readonly number[]): AlertClientContact | undefined => {
    const idSet = new Set(ids)
    const next = nextRowAfterArchive(contacts ?? [], ids, selectedRow.current)
    // `persistState(keepSelected ? selectedClientContactId : undefined, …)` — see the candidates.
    listIdsRef.current = listIdsRef.current.filter((id) => !idSet.has(id))
    const pageIds = listIdsRef.current.slice((currentPage - 1) * CONTACT_PAGE_SIZE, currentPage * CONTACT_PAGE_SIZE)
    const kept = selectedRow.current !== undefined && pageIds.includes(selectedRow.current) ? selectedRow.current : undefined
    selectedRow.current = kept
    persistListState(kept)
    setContacts((previous) => (previous === undefined ? previous : withoutRows(previous, idSet)))
    setTotalResults((total) => (total === undefined ? total : Math.max(0, total - ids.length)))
    setTabData((previous) => {
      const entry = previous[alertId]
      return entry === undefined
        ? previous
        : { ...previous, [alertId]: { ...entry, totalResults: Math.max(0, entry.totalResults - ids.length) } }
    })
    return next
  }

  /**
   * `exportClientContactsDecisionData` after the plain ATS export: exported and viewed, out of the
   * list unless it is the Exported (or actioned) view, then `resetCheckboxesAndCloseSnippet`.
   */
  const onContactsExportedToAts = (alertId: number, ids: readonly number[]): void => {
    const idSet = new Set(ids)
    if (shownOption.showExportedClientContactsOnly || shownOption.showActionedAndUnactionedClientContacts) {
      setContacts((previous) => previous?.map((row) => (idSet.has(row.id) ? { ...row, exported: true, viewed: true } : row)))
    } else {
      removeFromList(alertId, ids)
    }
    clearCheckedEntities(alertId)
    if (openContactId !== undefined) void navigate(`/chrome-extension/extension/client-contact/review?alertId=${String(alertId)}`)
  }

  /** The profile behind this list emits its flag changes; apply them here (component:1661). */
  useShortlistFlagEvents((event) => {
    const alert = current?.alert
    if (event.entity !== 'clientContacts' || alert === undefined || event.alertId !== alert.id) return
    if (event.kind === 'hide') {
      onContactsHidden(alert.id, event.ids, event.hiddenDate)
      return
    }
    const ids = new Set(event.ids)
    setContacts((previous) =>
      previous?.map((row) => {
        if (!ids.has(row.id)) return row
        if (event.kind === 'favor') return applyFavor(row)
        if (event.kind === 'unfavor') return applyUnfavor(row)
        return applyUnhide(row)
      }),
    )
  })

  /**
   * A profile inside this review asking to message its entity (`OnSingle…MessageClick`): exactly
   * what the row's Message does — tracked here, ADDED to the selection, then the composer at
   * `…-messages?alertId=…`. See `requestShortlistMessage`.
   */
  useShortlistMessageRequests((request) => {
    const alert = current?.alert
    if (request.entity !== 'clientContacts' || alert === undefined || request.alertId !== alert.id) return
    const row = (contacts ?? []).find((candidate) => candidate.id === request.row.id) ?? (request.row as AlertClientContact)
    onRowAction(row, { name: ProfileAction.Message } as ProfileActionDefinition)
  })

  /** The contacts twin of the candidate review's export request (`OnSingleClientContactExportClick`). */
  useShortlistExportRequests((request) => {
    const alert = current?.alert
    if (request.entity !== 'clientContacts' || alert === undefined || request.alertId !== alert.id) return
    const row = (contacts ?? []).find((candidate) => candidate.id === request.row.id) ?? (request.row as AlertClientContact)
    onRowAction(row, { name: ProfileAction.Export } as ProfileActionDefinition)
  })

  if (containers === undefined) return <div className="client-contact-hunter-review" />
  // The contacts list paginates client-side exactly as the candidates list does — the
  // endpoint takes no paging parameters and this screen was rendering all 102 rows under a
  // pager that read "1 / 1". See `CANDIDATE_PAGE_SIZE` in ShortlistReview for the same note.
  const pageContacts = contacts?.slice((currentPage - 1) * CONTACT_PAGE_SIZE, currentPage * CONTACT_PAGE_SIZE)
  const pagedTotal = Math.min(totalResults ?? contacts?.length ?? 0, CONTACT_MAX_RESULTS)
  const lastPage = Math.max(1, Math.ceil(pagedTotal / CONTACT_PAGE_SIZE))
  // The pager's `ng-if`, `data.maxResults > pageSize`, on the uncapped total — see
  // `showPager` in ShortlistReview, including the hunter-search half still owed to 6.4.
  const showPager = (totalResults ?? contacts?.length ?? 0) > CONTACT_PAGE_SIZE

  const current = selectedAlert
  if (current === undefined) return <div className="client-contact-hunter-review" />

  // One clock for the whole list — see the note on the candidate screen. Every row renders a
  // relative date, and reading `Date.now()` per row lets two rows a millisecond apart disagree
  // across a minute boundary.
  const renderedAt = Date.now()

  /**
   * One contact's hover action — the contacts twin of `ShortlistReview.onRowAction`, and the
   * reason `ClientContactRow` was built to take `actions` before it had anything to pass.
   */
  const onRowAction = (contact: AlertClientContact, action: ProfileActionDefinition): void => {
    const alert = current?.alert
    if (alert === undefined) return

    // `messageClientContact:787` — ADD the row to the selection, then message the whole of it.
    if (action.name === ProfileAction.Message) {
      trackClientContact.message(contact)
      const next = new Set(selectedIds)
      next.add(contact.id)
      setSelectedIds(next)
      trackClientContactSelection.checked(alert.id, contact, true)
      openMessaging(alert, [...next])
      return
    }

    // `onExportClientContact` (cee70a51e8) — tick the row, then export the selection, as the bar does.
    if (action.name === ProfileAction.Export) {
      trackClientContact.exportClicked(contact, 'list')
      const next = new Set(selectedIds)
      next.add(contact.id)
      setSelectedIds(next)
      trackClientContactSelection.checked(alert.id, contact, true)
      if (exportToExistingList) {
        void navigate(contactPanelPath('export', alert))
        return
      }
      setExportingToAts([...next])
      return
    }

    if (action.name === ProfileAction.Favor) {
      const undo = contact.favorite === true
      if (undo) trackClientContact.unfavor(contact, 'list')
      else trackClientContact.favor(contact, 'list')
      void Promise.resolve(
        undo ? unfavorClientContact(alert.id, contact.id) : favorClientContacts(alert.id, [contact.id]),
      ).then(
        () => {
          setContacts((previous) =>
            previous?.map((row) => (row.id === contact.id ? (undo ? applyUnfavor(row) : applyFavor(row)) : row)),
          )
        },
        () => undefined,
      )
      return
    }

    if (action.name === ProfileAction.Archive) {
      if (contact.hidden === true) trackClientContact.unhide(contact, 'list')
      else trackClientContact.hide(contact, 'list')
      if (contact.hidden === true) {
        void unhideClientContact(alert.id, contact.id).then(
          () => {
            setContacts((previous) => previous?.map((row) => (row.id === contact.id ? applyUnhide(row) : row)))
          },
          () => undefined,
        )
        return
      }
      void hideClientContacts(alert.id, [contact.id]).then(
        (hiddenDate) => {
          onContactsHidden(alert.id, [contact.id], hiddenDate)
        },
        () => undefined,
      )
    }
  }

  /**
   * "All in results" — every contact the FILTER matches, not the ten on screen. The candidates
   * twin of this has been here since Phase 3; this screen selected the LOADED list instead,
   * because `getClientContactDetailsByAlertId` was unported. It is the same shape as the
   * candidate call, takes the same parameters as the list — sending a different filter would
   * select contacts the recruiter cannot see — and MERGES rather than replaces, so a row
   * ticked by hand before the menu opened stays ticked.
   */
  /** `onSelectAllInPageClicked` — the page is ADDED to the selection, as on the candidates screen. */
  const selectAllInPage = (alertId: number): void => {
    const next = new Set([...selectedIds, ...(pageContacts ?? []).map((contact) => contact.id)])
    setSelectedIds(next)
    trackClientContactSelection.multi(alertId, 'selectAllInPage', next.size)
  }

  const selectAllInResults = async (): Promise<void> => {
    const alert = current?.alert
    if (alert === undefined) return
    try {
      const response = await getClientContactDetailsByAlertId({
        alertId: alert.id,
        sortBy: sortOption.field,
        filter: buildClientContactFilter(shownOption),
      })
      const next = new Set([...selectedIds, ...response.details.map((detail) => detail.id)])
      setSelectedIds(next)
      trackClientContactSelection.multi(alert.id, 'selectAllInResults', next.size)
      setAllDetails(response.details)
    } catch {
      // As on the candidates screen: the selection does not grow rather than the bar claiming
      // a count it does not have.
    }
  }

  const confirmDelete = (): void => {
    const alert = deleting
    setDeleting(undefined)
    if (alert === undefined) return
    void deleteAlertById(AlertEntity.ClientContacts, alert.id).then(
      () => {
        setContainers((previous) => previous?.filter((container) => container.alert.id !== alert.id))
        const next = containers?.find((container) => container.alert.id !== alert.id)?.alert.id
        void navigate(
          next === undefined
            ? '/chrome-extension/extension/client-contact/review'
            : `/chrome-extension/extension/client-contact/review?alertId=${String(next)}`,
        )
      },
      () => undefined,
    )
  }

  const contactPanelPath = (leaf: string, alert: { id: number }, extra?: Record<string, string>): string => {
    const query = new URLSearchParams({ alertId: String(alert.id), ...extra })
    return `/chrome-extension/extension/client-contact/review/${leaf}?${query.toString()}`
  }

  /**
   * `attemptMessageClientContacts:824` — refuse a selection nobody in which can be mailed, then
   * resolve the mailbox and open the composer or `email-signin`. An empty selection is allowed.
   */
  const openMessaging = (alert: { id: number }, ids: readonly number[]): void => {
    if (!hasValidIdsForMessagingOrEmpty(ids, contacts ?? [], allDetails)) {
      setNoValidContactsOpen(true)
      return
    }
    void getClientContactEmailIntegrationDetails().then(
      (details) => {
        if (details.enabled) {
          void navigate(contactPanelPath('client-contact-messages', alert))
          return
        }
        void navigate(
          contactPanelPath(
            'email-signin',
            alert,
            details.emailServerType === null ? undefined : { emailServerType: details.emailServerType },
          ),
        )
      },
      () => undefined,
    )
  }

  /**
   * The bar's actions. As on the candidates screen, Message and the existing-list Export proceed
   * with nothing ticked; Favor, Archive and the plain Export go through
   * `verifyHasSelectedClientContacts` (`:529`) — the "Select candidates" modal when the selection
   * is empty — and then run against whatever it left selected.
   *
   * ⚠️ MESSAGE AND EXPORT NAVIGATE: upstream's handlers (`attemptMessageClientContacts:828`,
   * `goToExportClientContactsState:979`) are state transitions, and the panel owns the write.
   */
  const onBulkAction = (action: BulkAction): void => {
    const alert = current?.alert
    if (alert === undefined) return

    if (action.name === ProfileAction.Message) {
      trackClientContactBulk.message(alert.id, [...selectedIds], contacts ?? [])
      openMessaging(alert, [...selectedIds])
      return
    }

    if (action.name === ProfileAction.Export && exportToExistingList) {
      trackClientContactBulk.exportPanel(alert.id, [...selectedIds], contacts ?? [])
      void navigate(contactPanelPath('export', alert))
      return
    }

    if (selectedIds.size === 0) {
      setMultiSelectAction(action)
      return
    }
    runSelectedAction(alert, action)
  }

  /** The gated half of `onBulkAction`; reads the selection from the store, not the render. */
  const runSelectedAction = (alert: { id: number }, action: BulkAction): void => {
    const ids = getCheckedEntities(alert.id).map((entry) => entry.id)
    if (ids.length === 0) return
    const idSet = new Set(ids)

    if (action.name === ProfileAction.Favor) {
      trackClientContactBulk.favor(alert.id, ids, contacts ?? [])
      void favorClientContacts(alert.id, ids).then(
        () => {
          setContacts((previous) => previous?.map((row) => (idSet.has(row.id) ? applyFavor(row) : row)))
          setSelectedIds(new Set())
        },
        () => undefined,
      )
      return
    }

    if (action.name === ProfileAction.Archive) {
      trackClientContactBulk.hide(alert.id, ids, contacts ?? [])
      void hideClientContacts(alert.id, ids).then(
        (hiddenDate) => {
          onContactsHidden(alert.id, ids, hiddenDate)
          setSelectedIds(new Set())
        },
        () => undefined,
      )
    }
    // `onExportClientContacts` — the async ATS export dialogue.
    if (action.name === ProfileAction.Export) setExportingToAts(ids)
  }

  /** `extensionAlertsTabsData` — one call per alert, cached by id. */
  const loadTabData = (): void => {
    for (const container of containers ?? []) {
      const id = container.alert.id
      if (tabData[id] !== undefined) continue
      void getClientContactNumbersByAlertId(id).then(
        (numbers) => {
          setTabData((previous) => ({ ...previous, [id]: numbers }))
        },
        () => undefined,
      )
    }
  }

  return (
    <div className="client-contact-hunter-review">
      <ExtensionAlertsHost entity="clientContacts" flexBasis={ExtensionViewWidth.Slim}>
        <div className="shortlist-content-container shortlist-content-container-slim">
          <SlimHeaderDropdown
            alert={current.alert}
            // The contact twin of `editExtensionAlert`, onto this product's own edit route.
            onEdit={() => {
              void navigate(
                `/chrome-extension/extension/client-contact/edit-alert?alertId=${String(current.alert.id)}`,
              )
            }}
            // The contacts twin of the candidates header: the NAME opens the alert list and
            // the ☰ opens its own menu. Neither had a handler here, so both were inert — the
            // same crossing `ShortlistReview` had, minus the alert-list panel, which needs
            // the contacts' own `SlimHeaderDropdownPanel` wiring.
            // ⚠️ THE NAME OPENS THE ALERT LIST, AND IT DID NOTHING HERE. Both products render
            // the SAME `slim-header-dropdown` with `extension-product` as a binding
            // (`client-contact-extension-alerts.tpl.html:116`), so the contacts header has the
            // list too — measured beside build 354, where clicking "Contacts" opened it and
            // this build's did not.
            onToggleAlertList={() => {
              setPanelOpen((open) => {
                // Counts are fetched when the list is about to OPEN, per alert and once —
                // the candidate screen does the same on its own toggle.
                if (!open) loadTabData()
                return !open
              })
            }}
            alertListOpen={panelOpen}
            onToggleActions={() => {
              setSlimActionsOpen((open) => !open)
            }}
            actionsOpen={slimActionsOpen}
            {...(panelOpen
              ? {
                  panel: (
                    <SlimHeaderDropdownPanel
                      alerts={containers.map((container) => container.alert)}
                      currentAlertId={current.alert.id}
                      // `Humanize.pluralize(2, getEntityTypeDisplay(ClientContacts))` — "client
                      // contacts", two words. Production's row reads "102 client contacts".
                      entityPlural="client contacts"
                      // ⚠️ FALSE ON THIS PRODUCT BY CONSTRUCTION, NOT BY FLAG. The tooltip
                      // directive sets `automated-hunters-enabled` only when
                      // `extensionProduct === Candidates`
                      // (`slim.header.dropdown.tooltip.directive.ng1.ts:22`), so the job-search
                      // group does not belong to the contacts panel however the tenant's
                      // `AUTOMATED_HUNTERS` flag is set.
                      automatedHunters={false}
                      // The SERVICE flag, which is product-independent — see its note above.
                      automationEnabled={automationEnabled}
                      tabData={tabData}
                      onSelectAlert={(alert) => {
                        setPanelOpen(false)
                        void navigate(
                          `/chrome-extension/extension/client-contact/review?alertId=${String(alert.id)}`,
                        )
                      }}
                      onAddSearch={() => {
                        setPanelOpen(false)
                        void navigate(
                          `/chrome-extension/extension/client-contact/edit-alert?alertId=${String(NEW_ALERT_ID)}` +
                            `&originAlertId=${String(current.alert.id)}`,
                        )
                      }}
                    />
                  ),
                }
              : {})}
            // See the candidates screen: `isCriteriaAtsFiltersEnabled` is the literal `true`
            // upstream, so this item is never drawn in production.
            showAtsFilters={false}
            onAtsFilters={() => {
              setSlimActionsOpen(false)
              void navigate(
                `/chrome-extension/extension/client-contact/edit-alert?alertId=${String(current.alert.id)}&atsFilters=true`,
              )
            }}
            // ⚠️ FALSE ON THIS PRODUCT, AND NOT BY FLAG.
            // `setBeautifiedSearchCriteriaEnabled` hardcodes it off for ClientContacts
            // (`slim.header.dropdown.component.ng1.ts:133`), so the contacts ☰ offers three
            // items where the candidates' offers four.
            showSearchCriteria={false}
            onDuplicate={() => {
              setSlimActionsOpen(false)
              void navigate(
                `/chrome-extension/extension/client-contact/edit-alert?alertId=${String(NEW_ALERT_ID)}` +
                  `&duplicate=true&originAlertId=${String(current.alert.id)}`,
              )
            }}
            onDelete={() => {
              setSlimActionsOpen(false)
              setDeleting(current.alert)
            }}
            checkboxDependent={checkboxDependent}
            {...(checkboxOperationLabel === undefined ? {} : { checkboxOperationLabel })}
          />
          {multiSelectAction === undefined || current === undefined ? null : (
            <MultiSelectModal
              onSelectAllInPage={() => {
                selectAllInPage(current.alert.id)
              }}
              onSelectAllInResults={selectAllInResults}
              getCheckedCount={() => getCheckedEntities(current.alert.id).length}
              onClose={() => {
                const action = multiSelectAction
                setMultiSelectAction(undefined)
                runSelectedAction(current.alert, action)
              }}
            />
          )}
          {exportingToAts === undefined || current === undefined ? null : (
            // Mounted until `onClose`, not until the ✕ — see the candidates screen.
            <ExportToAtsModal
              entity="clientContacts"
              alertId={current.alert.id}
              alertName={current.alert.name ?? ''}
              ids={exportingToAts}
              {...(atsName === undefined ? {} : { atsName })}
              onExported={(ids) => {
                onContactsExportedToAts(current.alert.id, ids)
                trackClientContactBulk.exported(current.alert.id, ids, contacts ?? [])
              }}
              onOpenInAts={sendOpenHotlist}
              onClose={() => {
                setExportingToAts(undefined)
              }}
            />
          )}
          {!noValidContactsOpen ? null : (
            <NoValidCandidatesDialogue
              entity="contacts"
              onClose={() => {
                setNoValidContactsOpen(false)
              }}
            />
          )}
          {deleting === undefined ? null : (
            <DeleteAlertDialogue
              alertName={deleting.name}
              onConfirm={confirmDelete}
              onCancel={() => {
                setDeleting(undefined)
              }}
            />
          )}
          {/* The same two wrappers the candidate list uses, and for the same reason: they are
              the root selectors of `_extension-alert-candidates-slim.scss`, which styles BOTH
              products — `extension-alert-client-contacts-slim.tpl.html:1-2` reuses the
              candidate class names verbatim rather than declaring its own.

              ⚠️ NO AUTOMATION BANNER HERE, and its absence is deliberate upstream: the whole
              `hunter-automation-wrap` block is COMMENTED OUT in the client-contact template
              (RND-6910). Porting it back because the candidate template has it would put a
              control on screen that production does not show. */}
          <AlertContainerHost>
            <CandidateListHost entity="clientContacts" loading={contacts === undefined}>
              <div className="extension-alert-candidates-slim-container">
                <div className="extension-alert-candidates-slim-filters-and-selection">
                  <CandidateFiltersBar
                    currentSort={sortOption.display}
                    shownOptionName={shownOption.name}
                    sortOptions={CONTACT_SORT_OPTIONS}
                    sortOpen={sortOpen}
                    onToggleSort={() => {
                      setSortOpen((open) => !open)
                    }}
                    onSelectSort={(option) => {
                      setSortOpen(false)
                      if (option.field === sortOption.field) return
                      const chosen = CONTACT_SORT_OPTIONS.find((candidate) => candidate.field === option.field)
                      if (chosen === undefined) return
                      setSortOption(chosen)
                      setSortChosen(true)
                    }}
                    onToggleFilters={() => {
                      setFiltersOpen((open) => !open)
                    }}
                    filtersMenu={
                      filtersOpen ? (
                        <ClientContactFiltersTooltip
                          shownOption={shownOption}
                          shownOptions={shownOptions}
                          onSelectShown={(option) => {
                            setFiltersOpen(false)
                            // `changeShownClientContacts` records before it does anything else —
                            // re-picking the option on display included.
                            trackClientContactList.shownFilter(current.alert.id, option.name)
                            if (option.name === shownOption.name) {
                              // …and upstream searches again, which this screen skips.
                              recordClientContactSearch(current.alert.id, sortOption.field, buildClientContactFilter(shownOption), true)
                              return
                            }
                            setShownOption(option)
                            setFiltersChosen(true)
                          }}
                        />
                      ) : null
                    }
                    totalResults={totalResults}
                    checkboxDependent={checkboxDependent}
                    {...(checkboxOperationLabel === undefined ? {} : { checkboxOperationLabel })}
                  />
                  {/* `extension-alert-client-contacts-slim.tpl.html:75` — the Message History's
                      anchor, between the bars as in the candidate list. */}
                  <MessageHistoryTooltip {...messageHistory} />
                  {contacts === undefined ? null : (
                    <CandidateSelectionBar
                      actions={bulkActions(current.alert, { ...actionFlags, shownOptionName: shownOption.name })}
                      selectedCount={selectedIds.size}
                      selectMenuOpen={selectMenuOpen}
                      onToggleSelectMenu={() => {
                        setSelectMenuOpen((open) => !open)
                      }}
                      onSelectAllInPage={() => {
                        setSelectMenuOpen(false)
                        // `viewClientContacts(this.page)` — the PAGE, as on candidates.
                        selectAllInPage(current.alert.id)
                      }}
                      onSelectAllInResults={() => {
                        setSelectMenuOpen(false)
                        void selectAllInResults()
                      }}
                      onClearAll={() => {
                        setSelectMenuOpen(false)
                        setSelectedIds(new Set())
                        trackClientContactSelection.multi(current.alert.id, 'clearAllInPage', 0)
                      }}
                      overflowOpen={bulkOverflowOpen}
                      onToggleOverflow={() => {
                        setBulkOverflowOpen((open) => !open)
                      }}
                      onAction={(action) => {
                        setBulkOverflowOpen(false)
                        onBulkAction(action)
                      }}
                      checkboxDependent={checkboxDependent}
                      {...(checkboxOperationLabel === undefined ? {} : { checkboxOperationLabel })}
                    />
                  )}
                </div>
                <div
                  className="candidate-lister-container-slim nicer-scroller mac-style four-px-scroll"
                  id="results-slim-scrollable-container"
                >
                  {contacts === undefined ? null : (
                    <div className="ext-alerts-results-slim-wrap">
                      <div className="ext-alerts-results-slim">
                        {(pageContacts ?? []).map((contact) => (
                          <ClientContactRow
                            key={contact.id}
                            contact={contact}
                            checked={selectedIds.has(contact.id)}
                            onCheck={(checked) => {
                              setSelectedIds((previous) => {
                                const next = new Set(previous)
                                if (checked) next.add(contact.id)
                                else next.delete(contact.id)
                                return next
                              })
                              trackClientContactSelection.checked(current.alert.id, contact, checked)
                            }}
                            actions={rowActions}
                            onAction={(action) => {
                              onRowAction(contact, action)
                            }}
                            checkboxDependent={checkboxDependent}
                            messagingEnabled={messagingEnabled}
                            onOpenMessages={() => {
                              messageHistory.openFor(contact.id)
                            }}
                            showAlgorithm={showAlgorithm}
                            now={renderedAt}
                            previouslySelected={openContactId === contact.id}
                            onOpen={() => {
                              openContact(contact.id, contact.algorithm)
                            }}
                            // The hover chevron, which reaches the same screen the row does.
                            onPreviewResume={() => {
                              openContact(contact.id, contact.algorithm)
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {contacts === undefined || !showPager ? null : (
                    <div className="extension-alert-candidate-slim-pagination">
                      <CandidatePagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        onMove={(delta) => {
                          const page = Math.min(Math.max(currentPage + delta, 1), lastPage)
                          setCurrentPage(page)
                          document.getElementById('results-slim-scrollable-container')?.scrollTo({ top: 0 })
                          // `persistState` in `movePage` — see the candidate screen.
                          selectedRow.current = openContactId === undefined ? undefined : selectedRow.current
                          persistListState(selectedRow.current, page)
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CandidateListHost>
          </AlertContainerHost>
        </div>
      </ExtensionAlertsHost>
      <div className={`shortlist-hunter-review-side-view${sideView === null ? '' : ' rendered'}`}>{sideView}</div>
    </div>
  )
}
